import React from "react";
import GenericCard from "@ui-components/Card/GenericCard";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import CardHeading from "../overview/CardHeading";
import type { AttentionItem, ReadingCoverage, Severity } from "./needsAttention";

export const SEVERITY_TAG: Record<Severity, { label: string; variant: "error" | "warning" | "neutral" }> = {
    critical: { label: "Critical", variant: "error" },
    attention: { label: "Attention", variant: "warning" },
    review: { label: "Review", variant: "neutral" },
};

/** "1 attention · 2 to review" — or nothing to say. */
export function attentionSummary(items: AttentionItem[]): string | null {
    const count = (s: Severity) => items.filter((i) => i.severity === s).length;
    const parts = [
        count("critical") ? `${count("critical")} critical` : null,
        count("attention") ? `${count("attention")} attention` : null,
        count("review") ? `${count("review")} to review` : null,
    ].filter(Boolean);
    return parts.length ? parts.join(" · ") : null;
}

/**
 * One item, with no button (CTX-1525): "Inspect" opened an inspector that began by
 * repeating the item word for word, so in production it read as doing nothing. A node's
 * details stay one click away on the map, and the header's pill still goes there.
 */
function Item({ item }: { item: AttentionItem }) {
    const tag = SEVERITY_TAG[item.severity];
    return (
        <li className="flex gap-3 items-start py-3 border-b border-semantic-border-primary last:border-b-0">
            <GenericTag label={tag.label} variant={tag.variant} size="small" minWidth="76px" />
            <div className="flex-1 min-w-0 flex flex-col gap-1">
                {/* Size and color carry the hierarchy, not weight: the title in the primary
                    color, the detail a step smaller in the secondary (CTX-1527, review of 27-oct). */}
                <span className="text-sm text-semantic-text-primary">{item.title}</span>
                <span className="text-xs text-semantic-text-secondary">{item.detail}</span>
                {item.evidence.length > 0 && (
                    <span className="flex flex-wrap gap-1.5 mt-1">
                        {item.evidence.map((e) => (
                            <GenericTag key={e.label} label={e.label} variant={e.tone === "ok" ? "success" : "neutral"} size="small" />
                        ))}
                    </span>
                )}
            </div>
        </li>
    );
}

/** An empty list, by how much could be read: never "nothing" when nothing was read (CTX-1116). */
const EMPTY_LIST: Record<ReadingCoverage, string> = {
    full: "No observations.",
    partial: "No observations in what could be read.",
    none: "No reading: the firewall could not be checked.",
};

/**
 * What we observe on this firewall (CTX-1208), most serious first. The list comes from
 * `needsAttention`, the one place that decides it; this card only shows it. Titled
 * "Observations" (CTX-1528): the customer cannot change the firewall, so "Needs
 * attention" asked them for something they could not do.
 */
export default function NeedsAttentionCard({
    items,
    loading,
    coverage,
}: {
    items: AttentionItem[];
    loading: boolean;
    /** How much of what the list looks at could be read (`readingCoverage`). */
    coverage: ReadingCoverage;
}) {
    const summary = attentionSummary(items);
    // As loud as the most serious item, like the header's pill (CTX-1526).
    const worst = (["critical", "attention", "review"] as const).find((s) => items.some((i) => i.severity === s)) ?? "review";
    return (
        // Fills its grid cell, so its bottom lines up with Device health's beside it.
        <GenericCard isCollapsible={false} variant="primary" shadow="sm" className="h-full">
            <section className="p-5" aria-label="Observations" aria-busy={loading}>
                {/* The summary drops under the title rather than wrapping inside its pill (CTX-1391). */}
                <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2 mb-2">
                    <CardHeading title="Observations" subtitle="Only this firewall · recent log and current state" />
                    {summary && (
                        <span className="shrink-0 whitespace-nowrap">
                            <GenericTag label={summary} variant={SEVERITY_TAG[worst].variant} size="small" />
                        </span>
                    )}
                </div>
                {items.length === 0 ? (
                    <p className="text-sm text-semantic-text-secondary py-2">{loading ? "Checking…" : EMPTY_LIST[coverage]}</p>
                ) : (
                    // Beside Device health (from lg, where the page goes to two columns), about
                    // three items of 65 px and a scroll for the rest, so Device health is not
                    // left half empty (CTX-1527), as Licenses beside Policies (CTX-1272). Most
                    // serious first and the pill counts them all; stacked, the whole list.
                    <ul className="custom-scrollbar lg:max-h-[195px] lg:overflow-y-auto">
                        {items.map((item) => (
                            <Item key={item.id} item={item} />
                        ))}
                    </ul>
                )}
                {items.length > 0 && !loading && coverage !== "full" && (
                    <p className="text-xs text-semantic-text-secondary pt-2">Some readings were unavailable; there may be more.</p>
                )}
            </section>
        </GenericCard>
    );
}
